import React, { useCallback } from 'react'

import {
  CONVERSATION_FACTS_DIMENSIONS,
  EXPORT_MESSAGE_BY_STATUS,
  formatExportFileName,
  useCubeReportAccessFiltersContext,
  useCubeReportPageFiltersContext,
} from 'cube'
import { ConversationCountDrillDownBaseQuery } from 'cube/queries/ConversationCountDrillDown.query'
import { ConversationExportBaseQuery } from 'cube/queries/ConversationCountExport.query'
import type { PointClickEventObject } from 'highcharts'
import moment from 'moment'

import { Button, Card, Flex, Header, Text, useToast } from '@driftt/dds'
import {
  CubeQueryBuilder,
  CubeQueryStringAdapter,
  getStartOfSeriesTimestamp,
  LineChart,
  useCubeExport,
} from '@driftt/dds.reports'
import { tracking } from '@driftt/traffic-control'

import { useTrackDrillDownClickEvent } from 'components/conversation/hooks/useTrackDrilldownClickEvent'
import useDateRange from 'hooks/useDateRange'
import useTimePeriod from 'hooks/useTimePeriod'
import { CONVO_DETAIL_REPORT_PATH } from 'layouts/ConversationsDetailReport/ConversationsDetailReport.constants'

import { useConversationCountsByDay } from './useConversationCounts.hook'

export const ConversationCountLineChart = () => {
  const { legendText } = useDateRange()
  const triggerToast = useToast()
  const timePeriod = useTimePeriod()
  const { pageFilters } = useCubeReportPageFiltersContext()
  const { accessFilters } = useCubeReportAccessFiltersContext()

  const { trackViewEvent } = useTrackDrillDownClickEvent(
    'ConversationCountsLineChart'
  )

  const exportCubeDataQuery = new CubeQueryBuilder({
    query: ConversationExportBaseQuery,
  })
    .withName('ConversationCountsExportQuery')
    .withinDateRange(
      CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_AT,
      timePeriod.startDate.toDate(),
      timePeriod.endDate.toDate()
    )
    .withFilters([...pageFilters, ...accessFilters])
    .build()

  const { isInitialLoading: isDownloadingExport, refetch: exportCubeData } =
    useCubeExport({
      query: exportCubeDataQuery,
      enabled: false,
      exportFileName: formatExportFileName(
        'Chats',
        timePeriod.startDate.toDate(),
        timePeriod.endDate.toDate()
      ),
      onExportSuccess() {
        triggerToast({
          message: EXPORT_MESSAGE_BY_STATUS.SUCCESS,
          variant: 'success',
        })
      },
      onExportError() {
        triggerToast({
          message: EXPORT_MESSAGE_BY_STATUS.ERROR,
          variant: 'error',
        })
      },
    })

  const { metric, previousMetric, isLoading, metricTotal } =
    useConversationCountsByDay()

  const onExportButtonClick = useCallback(() => {
    tracking.trackEvent('CUBE_DRILL_DOWN_EXPORT_CLICK', {
      metricType: 'ConversationCountsLineChartExport',
      startDate: timePeriod.startDate.toDate(),
      endDate: timePeriod.endDate.toDate(),
      pageName: window.location.pathname,
      rowSize: metricTotal,
    })
    exportCubeData()
  }, [exportCubeData, metricTotal, timePeriod.startDate, timePeriod.endDate])

  const onChartPointClick = useCallback(
    (evt: PointClickEventObject) => {
      if (evt.point.y === 0) {
        return
      }

      const startDate = moment(evt.point.name).startOf('D')
      const endDate = moment(evt.point.name).endOf('D')

      const hydratedDrillDownQuery = new CubeQueryBuilder({
        query: ConversationCountDrillDownBaseQuery,
      })
        .withName('ConversationCountsDrillDownPoint')
        .withinDateRange(
          CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_AT,
          startDate.toDate(),
          endDate.toDate()
        )
        .withFilters([...pageFilters, ...accessFilters])
        .build()

      const totalRecords = evt.point.y ?? 0

      trackViewEvent({ totalRecords })

      const drillDownQueryString = CubeQueryStringAdapter.toQueryString(
        hydratedDrillDownQuery
      )
      const drillDownURL = `${CONVO_DETAIL_REPORT_PATH}?${drillDownQueryString}`
      window.open(drillDownURL, '_blank')
    },
    [pageFilters, accessFilters, trackViewEvent]
  )

  return (
    <Card
      css={{ gridColumn: 'span 2', minHeight: 495 }}
      rootProps={{ id: 'chats-report--conversation-line-chart-card' }}
    >
      <Card.Header as={Flex} justifyContent="space-between">
        <Flex direction="column">
          <Header variant="h3">New chats</Header>
          <Text variant="metadata">{legendText.series}</Text>
        </Flex>
        <Button
          variant="secondary"
          loading={isDownloadingExport}
          onClick={() => onExportButtonClick()}
          rootProps={{
            id: 'chats-report--conversation-line-chart-export-button',
          }}
        >
          Export CSV
        </Button>
      </Card.Header>
      <Card.Content as={Flex} fullHeight>
        <LineChart
          isLoading={isLoading}
          onPointClick={onChartPointClick}
          series={[
            {
              data: metric,
              label: legendText.series,
              pointStart: getStartOfSeriesTimestamp(metric),
            },
            {
              data: previousMetric,
              label: legendText.previousSeries,
              dashStyle: 'LongDash',
              pointStart: getStartOfSeriesTimestamp(metric),
            },
          ]}
          tooltipXValueFormat="DATE"
          hideTooltipHeader
        />
      </Card.Content>
    </Card>
  )
}
